import React from 'react';
import styled from 'styled-components';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import {Grid} from '@material-ui/core';
import {
    TextCard,
    TextCardProps,
    Typography,
    VerticalSpacer,
    GridContainer,
    EyebrowProps,
    ImageProps,
    AboveFoldScrollIndicator,
    LicenseCard,
    LicenseCardProps
} from '../../elements';
import {AnimateOnViewWrap} from '../../wraps/animation/AnimateOnViewWrap';

const Styled = styled.div<{$backgroundImg: string; backgroundColorInitial?: boolean}>`
    position: relative;

    .hero-text-wrapper {
        background-image: url(' ${(p) => p.$backgroundImg} ');
        background-repeat: no-repeat;
        position: relative;
        color: ${(p) => p.theme.palette.text.primary};
        background-color: ${(p) =>
            p.backgroundColorInitial
                ? 'initial'
                : p.theme.ifLight(
                      p,
                      p.theme.palette.common.white,
                      p.theme.palette.background.paper
                  )};
        background-position: top -96px left -96px;
        ${(p) => p.theme.breakpoints.up('md')} {
            padding-bottom: 72px;
            background-position: top -96px left -172px;
        }
    }

    .MuiGrid-item {
        padding: 0px;
    }

    .hero-img {
        position: relative;
        top: -72px;
    }

    .stackedSocialIcons {
        position: relative;
        top: 13px;
    }

    .text-cards-with-bordertop .text-card {
        max-width: 200px;
        border-top: 2px solid #000;
    }

    #textCard .heading.h3 {
        font-weight: 400;
    }

    .ue-link-wrap .button {
        font-weight: 400;
        text-transform: none;
        ${(p) => p.theme.breakpoints.down('sm')} {
            white-space: normal;
        }
    }

    .finePrint {
        // to overwrite #__next rules, which should be different for other tinyCaptions
        ${(p) => p.theme.breakpoints.up('md')} {
            font-size: 12px !important;
            line-height: 18px !important;
        }
        ${(p) => p.theme.breakpoints.down('sm')} {
            font-size: 14px !important;
            line-height: 22px !important;
        }
    }

    .hero-text-heading .heading {
        font-size: initial;
        font-weight: initial;
    }

    .text-card .description {
        max-width: 300px;
    }
`;

const StyledAboveFoldScrollIndicator = styled.div`
    width: 83px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

export interface DownloadHeroUE5Props {
    backgroundImg?: ImageProps;
    eyebrow?: EyebrowProps;
    title?: string;
    accentColor?: AccentColorOptions;
    textCards?: TextCardProps[];
    textCardLeftPanel?: TextCardProps;
    backgroundColorInitial?: boolean;
    licenseCard?: LicenseCardProps;
}

export const DownloadHeroUE5 = ({
    backgroundImg,
    eyebrow,
    title,
    textCardLeftPanel,
    backgroundColorInitial,
    licenseCard
}: DownloadHeroUE5Props): JSX.Element | null => {
    const isSm = useBreakpointDown('sm');
    const leftPanelGridCols = isSm ? 12 : 5;

    return (
        <Styled
            className={'download-hero'}
            $backgroundImg={backgroundImg?.src || ''}
            backgroundColorInitial={backgroundColorInitial}>
            <div className="hero-text-wrapper">
                <VerticalSpacer variant="custom" spacing={16} />
                <GridContainer>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="stretch">
                        <Grid item md={1} />
                        <Grid item md={leftPanelGridCols}>
                            <AnimateOnViewWrap translateY={20}>
                                {eyebrow && eyebrow.text && (
                                    <>
                                        <Grid item>
                                            <Typography variant="h4" big as="span">
                                                {eyebrow.text}
                                            </Typography>
                                            <VerticalSpacer variant="custom" spacing={4} />
                                        </Grid>
                                    </>
                                )}
                                {title && (
                                    <>
                                        {!eyebrow && <VerticalSpacer variant="heading" />}
                                        <Typography className="hero-title" variant="h1">
                                            {title}
                                        </Typography>
                                    </>
                                )}

                                {textCardLeftPanel && (
                                    <>
                                        <VerticalSpacer variant="heading" />
                                        <TextCard {...textCardLeftPanel} disableAnim={true} />
                                    </>
                                )}
                            </AnimateOnViewWrap>
                            {isSm && <VerticalSpacer variant="section" />}
                        </Grid>

                        <Grid item md={1} />
                        <Grid className="text-cards-with-bordertop" item xs={12} sm={12} md={4}>
                            <AnimateOnViewWrap delay={0.5} translateY={30}>
                                <Grid
                                    container
                                    spacing={2}
                                    direction="row"
                                    justifyContent={isSm ? 'center' : 'flex-end'}
                                    alignItems="stretch">
                                    {licenseCard && (
                                        <LicenseCard
                                            {...licenseCard}
                                            widthContained={true}
                                            active={true}
                                            hidePrice={true}
                                        />
                                    )}
                                </Grid>
                            </AnimateOnViewWrap>
                        </Grid>
                        <Grid item md={1} />
                    </Grid>
                </GridContainer>
            </div>
            <AnimateOnViewWrap delay={0.8} translateY={30}>
                <GridContainer>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="stretch">
                        <Grid item md={1}>
                            &nbsp;
                        </Grid>
                        <Grid item sm={12} md={1}>
                            <StyledAboveFoldScrollIndicator className="ue-scroll-indicator">
                                {!isSm && <AboveFoldScrollIndicator forceSmall translateY={0} />}
                            </StyledAboveFoldScrollIndicator>
                        </Grid>
                        <Grid item sm={12} md={10}></Grid>
                    </Grid>
                </GridContainer>
            </AnimateOnViewWrap>
        </Styled>
    );
};
